import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
} from 'recharts';
import { ChevronDown } from 'lucide-react';
import './JobActivityChart.scss';

const JobActivityChart = ({ selectedLocation, locumHoursData, onFilterChange }) => {
   const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [selectedView, setSelectedView] = useState('Year');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth().toString());
  const [selectedWeek, setSelectedWeek] = useState('1');
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [weeksInMonth, setWeeksInMonth] = useState(['Week 1', 'Week 2', 'Week 3', 'Week 4']);

  // const years = ['2023', '2024', '2025'];
    const years = Array.from({ length: 3 }, (_, i) =>
      String(currentYear - 2 + i)
    );
    console.log("years",years)
  const views = ['Year', 'Monthly', 'Daily'];
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const getWeeksInMonth = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
  
    const firstWeekDay = firstDay.getDay(); // 0 (Sunday) to 6 (Saturday)
    const totalDays = lastDay.getDate();
  
    // Calculate total weeks (partial weeks at start and end count as full weeks)
    const totalWeeks = Math.ceil((firstWeekDay + totalDays) / 7);
  
    // Return array of week numbers [1, 2, 3, 4, 5] or [1, 2, 3, 4, 5, 6] etc.
    return Array.from({ length: totalWeeks }, (_, i) => `Week ${i + 1}`);
  };

  const transformData = (data, view) => {
    const allMonths = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    const allWeeks = ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6'];
    const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    const safeData = data?.totalJobsPostedGraph || [];

    // For Daily view
    if (view === 'Daily') {
      return allDays.map(day => {
        const found = safeData.find(d => {
          const dayMatch = d.day && d.day.toLowerCase() === day.toLowerCase();
          const monthMatch = !selectedMonth || (d.month && d.month.toLowerCase() === months[parseInt(selectedMonth)]?.substring(0, 3).toLowerCase());
          const weekMatch = !selectedWeek || (d.week && 
            (d.week === selectedWeek || 
             d.week === parseInt(selectedWeek.split(' ')[1])));
          return dayMatch && monthMatch && weekMatch;
        });
        return {
          name: day.substring(0, 3),
          fullLabel: day,
          value: found ? parseInt(found.count || 0) : 0
        };
      });
    }

    // For Weekly view
    if (view === 'Weekly') {
      const selectedMonthIndex = parseInt(selectedMonth);
      const selectedMonthName = months[selectedMonthIndex]?.substring(0, 3);

      return allWeeks.map(week => {
        const weekNumber = week.split(' ')[1];
        const found = safeData.find(d => {
          // Handle both string and number week formats
          const dataWeek = d.week?.toString().includes('Week') ? d.week : `Week ${d.week}`;
          const weekMatch = dataWeek === week || dataWeek === weekNumber;
          const monthMatch = d.month && d.month.substring(0, 3).toLowerCase() === selectedMonthName?.toLowerCase();
          return weekMatch && monthMatch;
        });
        
        return {
          name: `Week ${weekNumber}`,
          fullLabel: week,
          value: found ? parseInt(found.count || 0) : 0
        };
      });
    }

    // Default: Monthly view (Yearly)
    return allMonths.map(month => {
      const found = safeData.find(d => {
        if (!d.month) return false;
        const apiMonth = d.month.substring(0, 3).toLowerCase();
        return apiMonth === month.toLowerCase();
      });
      return {
        name: month,
        fullLabel: month,
        value: found ? parseInt(found.count || 0) : 0
      };
    });
  };

  useEffect(() => {
    setIsLoading(true);
  
    const timeout = setTimeout(() => {
      if (locumHoursData) {
        const transformed = transformData(locumHoursData, selectedView);
        setChartData(transformed);
      }
      setIsLoading(false);
    }, 100); // optional slight delay for UX
  
    return () => clearTimeout(timeout);
  }, [locumHoursData, selectedView, selectedMonth, selectedWeek]);

  useEffect(() => {
    if (selectedMonth !== '') {
      const monthIndex = parseInt(selectedMonth);
      const year = parseInt(selectedYear);
      const weeks = getWeeksInMonth(year, monthIndex);
      setWeeksInMonth(weeks);
      
      // Reset selected week if it's greater than the number of weeks in the new month
      if (selectedWeek) {
        const weekNum = parseInt(selectedWeek.split(' ')[1]);
        if (weekNum > weeks.length) {
          setSelectedWeek(weeks[weeks.length - 1]);
        }
      }
    }
  }, [selectedMonth, selectedYear]);

  const getSubtitle = () => {
    let subtitle = `Showing ${selectedView.toLowerCase()} data for ${selectedYear}`;
    const monthIndex = selectedMonth !== '' ? parseInt(selectedMonth) : new Date().getMonth();
    
    if (selectedView === 'Weekly' && !isNaN(monthIndex)) {
      subtitle += ` - ${months[monthIndex]}`;
    }
    if (selectedView === 'Daily' && !isNaN(monthIndex) && selectedWeek) {
      subtitle += ` - ${months[monthIndex]}, ${selectedWeek}`;
    }
    return subtitle;
  };

  const handleViewChange = (newViewLabel) => {
    // Map the view labels to their corresponding values
    const viewMap = {
      'Year': 'Monthly',
      'Monthly': 'Weekly',
      'Daily': 'Daily'
    };
    
    const viewToApiMap = {
      'Year': 'year',
      'Monthly': 'monthly',
      'Daily': 'daily'
    };
    
    const newView = viewMap[newViewLabel];
    setSelectedView(newView);
    
    // Set default week when switching to daily view
    if (newView === 'Daily' && !selectedWeek) {
      setSelectedWeek('Week 1');
    }
    
    // Get current month (0-11)
    const monthIndex = selectedMonth !== '' ? parseInt(selectedMonth) : new Date().getMonth();
    
    // Parse week number safely
    let weekNumber = 1;
    if (selectedWeek) {
      const weekMatch = selectedWeek.toString().match(/\d+/);
      weekNumber = weekMatch ? parseInt(weekMatch[0], 10) : 1;
    }
    
    // Call onFilterChange with the correct parameters
    onFilterChange && onFilterChange({
      jobGraphType: viewToApiMap[newViewLabel] || 'monthly',
      year: parseInt(selectedYear),
      month: monthIndex + 1, // Convert to 1-based month
      week: weekNumber
    });
  };

  // const handleYearChange = (year) => {
  //   setSelectedYear(year);
    
  //   // Map the current view to the correct jobGraphType
  //   const viewToApiMap = {
  //     'Year': 'year',
  //     'Monthly': 'monthly',
  //     'Weekly': 'monthly',
  //     'Daily': 'daily'
  //   };
    
  //   // Get current month (0-11)
  //   const monthIndex = selectedMonth !== '' ? parseInt(selectedMonth) : new Date().getMonth();
    
  //   // Parse week number safely
  //   let weekNumber = 1;
  //   if (selectedWeek) {
  //     const weekMatch = selectedWeek.toString().match(/\d+/);
  //     weekNumber = weekMatch ? parseInt(weekMatch[0], 10) : 1;
  //   }
    
  //   // Use the correct view mapping based on the current view
  //   const apiView = selectedView === 'Monthly' ? 'Monthly' : 
  //                  selectedView === 'Daily' ? 'Daily' : 'Year';
    
  //   onFilterChange({
  //     jobGraphType: viewToApiMap[apiView] || 'year',
  //     year: parseInt(year),
  //     month: monthIndex + 1, // Convert to 1-based month
  //     week: weekNumber
  //   });
  // };
  const handleYearChange = (year) => {
    setSelectedYear(year);
    
    // Map the current view to the correct jobGraphType
    const viewToApiMap = {
      'Year': 'year',
      'Monthly': 'monthly',
      'Weekly': 'monthly',
      'Daily': 'daily'
    };
    
    // Get current month (0-11)
    const monthIndex = selectedMonth !== '' ? parseInt(selectedMonth) : new Date().getMonth();
    
    // Parse week number safely
    let weekNumber = 1;
    if (selectedWeek) {
      const weekMatch = selectedWeek.toString().match(/\d+/);
      weekNumber = weekMatch ? parseInt(weekMatch[0], 10) : 1;
    }
    
    // Determine the correct API view based on the selected view
    let apiView = 'Year'; // Default to 'year' if view is not recognized
    if (selectedView === 'Monthly' || selectedView === 'Weekly') {
      apiView = 'Monthly';
    } else if (selectedView === 'Daily') {
      apiView = 'Daily';
    }
    
    onFilterChange({
      jobGraphType: viewToApiMap[apiView],
      year: parseInt(year),
      month: monthIndex + 1, // Convert to 1-based month
      week: weekNumber
    });
  };
  const handleWeekChange = (weekValue) => {
    // Ensure we have a valid week value
    const weekNum = parseInt(weekValue.split(' ')[1]);
    if (isNaN(weekNum)) return;
    
    setSelectedWeek(weekValue);
    
    // Get current month (0-11)
    const monthIndex = selectedMonth !== '' ? parseInt(selectedMonth) : new Date().getMonth();
    
    onFilterChange({
      jobGraphType: 'daily',
      year: parseInt(selectedYear),
      month: monthIndex + 1, // Convert to 1-based month
      week: weekNum
    });
  };

  const placeholderChartData = useMemo(() => {
    if (selectedView === 'Daily') {
      return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => ({
        name: day,
        fullLabel: day,
        value: 0
      }));
    }
  
    if (selectedView === 'Weekly') {
      return ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6'].map(week => ({
        name: week,
        fullLabel: week,
        value: 0
      }));
    }
  
    // Default for 'Year' view
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(month => ({
      name: month,
      fullLabel: month,
      value: 0
    }));
  }, [selectedView]);

  const getRoundedMax = (max) => {
    if (max <= 10) return 10;
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
    return Math.ceil(max / magnitude) * magnitude;
  };
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");

    // Set initial state
    setIsMobile(mediaQuery.matches);

    // Listener for changes
    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);
  const hasData = chartData.some(item => item.value > 0);

  return (
    <div className="job-activity-chart">
      <div className="job-activity-chart__header">
        <div>
          <h2 className="job-activity-chart__title">Job Activity Over Time</h2>
          <p className="job-activity-chart__subtitle">{getSubtitle()}</p>
        </div>
        <div className="job-activity-chart__filters">
          <div className="job-activity-chart__filter">
            <select
              value={selectedYear}
              onChange={(e) => handleYearChange(e.target.value)}
              className="job-activity-chart__select"
            >
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            {/* <ChevronDown className="job-activity-chart__select-icon" /> */}
          </div>

          <div className="job-activity-chart__filter">
            <select
              value={views.find(v => {
                const map = { Monthly: 'Year', Weekly: 'Monthly', Daily: 'Daily' };
                return map[selectedView] === v;
              })}
              onChange={(e) => handleViewChange(e.target.value)}
              className="job-activity-chart__select"
            >
              {views.map((label) => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
            {/* <ChevronDown className="job-activity-chart__select-icon" /> */}
          </div>

          {(selectedView === 'Weekly' || selectedView === 'Daily') && (
            <div className="job-activity-chart__filter">
              {selectedView === 'Weekly' || selectedView === 'Daily' ? (
                <select
                  value={selectedMonth}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedMonth(value); // store 0-based value like "5" for June
                    
                    // Determine the correct jobGraphType based on current view
                    const viewToApiMap = {
                      'Year': 'year',
                      'Monthly': 'monthly',
                      'Weekly': 'monthly',
                      'Daily': 'daily'
                    };
                    
                    // Parse week number safely if in daily view
                    let weekNumber = 1;
                    if (selectedWeek && selectedView === 'Daily') {
                      const weekMatch = selectedWeek.toString().match(/\d+/);
                      weekNumber = weekMatch ? parseInt(weekMatch[0], 10) : 1;
                    }
                    
                    onFilterChange({
                      jobGraphType: viewToApiMap[selectedView] || 'monthly',
                      year: parseInt(selectedYear),
                      month: parseInt(value) + 1, // Convert to 1-based month
                      week: selectedView === 'Daily' ? weekNumber : undefined
                    });
                  }}
                  className="job-activity-chart__select"
                >
                  {months.map((month, index) => (
                    <option key={month} value={index.toString()}>{month}</option>
                  ))}
                </select>
              ) : null}
              {/* <ChevronDown className="job-activity-chart__select-icon" /> */}
            </div>
          )}

          {selectedView === 'Daily' && (
            <div className="job-activity-chart__filter">
              <select
                value={selectedWeek || ''}
                onChange={(e) => handleWeekChange(e.target.value)}
                className="job-activity-chart__select"
              >
                {weeksInMonth.map((week) => (
                  <option key={week} value={week}>{week}</option>
                ))}
              </select>
              {/* <ChevronDown className="job-activity-chart__select-icon" /> */}
            </div>
          )}
        </div>
      </div>

      {/* Empty State */}
      {!hasData && (
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', marginTop: '20px'}}>
          <h3 className="job-activity-chart__title" style={{fontSize: '1.2rem', marginBottom: '10px'}}>No Activity Data Found</h3>
          <p style={{textAlign:'center', fontSize:'0.9rem', fontWeight:'400', color:'#1f2937', maxWidth: '400px', margin: '0 auto'}}>
            There is no job activity data recorded for the selected year.
          </p>
        </div>
      )}

      {/* Chart Container - Only show when there's data */}
      {hasData && (
      <div className="job-activity-chart__chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart  data={isLoading ? placeholderChartData : chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <XAxis
  dataKey="name"
  axisLine={false}
  tickLine={false}
  interval={0}
  angle={isMobile ? -40 : 0}
  minTickGap={1}
  height={50}
  tick={{
    fill: 'black',
    fontSize: isMobile ? 7 : 12,
    fontWeight: '400',
  }}
  tickFormatter={(value) => value}
/>
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{
                // fill: '#9CA3AF',
                fill: 'black',
                 fontSize: 12  , fontWeight:'400'}}
              // domain={[0, (dataMax) => Math.max(dataMax, 5)]}
              domain={[0, (dataMax) => getRoundedMax(dataMax)]}
            />
            {/* <Tooltip 
              formatter={(value) => [`${value} Jobs`, 'Total Jobs']}
              labelFormatter={(label) => label}
              cursor={{ fill: 'rgba(255, 254, 254, 0)' }}
            /> */}
            <Tooltip 
              formatter={(value, name, props) => [
                `${value} Jobs`, 
                props.payload.fullLabel || props.name
              ]}
              labelFormatter={() => ''} // We'll use the formatter for both label and value
              cursor={{ fill: 'rgba(255, 254, 254, 0)' }}
            />
            <Bar dataKey="value" radius={[2, 2, 0, 0]} maxBarSize={20}>

              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`}
                  fill={entry.value > 0 ? '#034585' : '#E5E7EB'}
                  stroke={entry.value > 0 ? '#034585' : '#E5E7EB'}
                  strokeWidth={1}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      )}

      <div className="job-activity-chart__legend" aria-label="Chart Legend">
        <div className="job-activity-chart__legend-item">
          <span className="job-activity-chart__legend-color job-activity-chart__legend-color--actual" />
          <span className="job-activity-chart__legend-text">Total Job Posted</span>
        </div>
        <div className="job-activity-chart__legend-item">
          {/* <span className="job-activity-chart__legend-color job-activity-chart__legend-color--prediction" /> */}
          {/* <span className="job-activity-chart__legend-text">Predictions</span> */}
        </div>
      </div>
    </div>
  );
};

export default JobActivityChart;
